import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Breadcrumb, Col, Row } from "antd";
import {
  CarOutlined,
  CreditCardOutlined,
  MinusOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

const App = dynamic(() => import("../../components/App"));
const icons = [
  ShopOutlined,
  ShoppingOutlined,
  CreditCardOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
];
const ranges = [
  [0, 5],
  [5, 11],
  [11, 17],
  [17, 22],
  [22, 24],
];

const aboutLuxury29 = {
  th: [
    {
      question: "Luxury29 คืออะไร?",
      answer:
        "Luxury29 เป็นร้านจำหน่ายสินค้าแฟชั่นและสินค้าลักชัวรีจากแบรนด์ชั้นนำระดับโลก รวมถึงสินค้าแฟชั่น รองเท้า และสนีกเกอร์จากหลากหลายแบรนด์ ลูกค้าสามารถเลือกซื้อสินค้าผ่านเว็บไซต์ แอปพลิเคชัน ช่องทางออนไลน์ และหน้าร้าน Luxury29",
    },
    {
      question: "Luxury29 มีหน้าร้านจริงหรือไม่?",
      answer:
        "มี Luxury29 มีหน้าร้านจริงทั้งหมด 5 สาขาในประเทศไทย ลูกค้าสามารถเข้าชมและเลือกซื้อสินค้าได้ที่หน้าร้าน Luxury29",
    },
    {
      question: "Luxury29 มีสาขาในจังหวัดไหนบ้าง?",
      answer:
        "Luxury29 มีหน้าร้านทั้งหมด 5 สาขาในประเทศไทย ได้แก่\nCentral Festival เชียงใหม่ ชั้น 1\nMAYA เชียงใหม่ ชั้น G\nCentral อุดรธานี ชั้น 1\nCentral Westgate จังหวัดนนทบุรี ชั้น 1\nCentral พิษณุโลก ชั้น 1\n\nทั้งนี้ เวลาเปิดให้บริการของแต่ละสาขาอาจแตกต่างกัน สามารถตรวจสอบรายละเอียดของแต่ละสาขาได้ด้านล่าง",
    },
    {
      question: "Luxury29 มีสาขาในเชียงใหม่กี่สาขา?",
      answer:
        "Luxury29 มี 2 สาขาในจังหวัดเชียงใหม่ ได้แก่ Central Festival เชียงใหม่ ชั้น 1 และ MAYA เชียงใหม่ ชั้น G",
    },
    {
      question: "Luxury29 เปิดกี่โมง?",
      answer:
        "Luxury29 มีหน้าร้านทั้งหมด 5 สาขา และเปิดให้บริการทุกวัน โดยเวลาเปิด-ปิดของแต่ละสาขาแตกต่างกัน ดังนี้\n\nCentral Festival เชียงใหม่ ชั้น 1\nจันทร์–พฤหัสบดี: 11:00–21:30 น.\nศุกร์: 11:00–22:00 น.\nเสาร์–อาทิตย์ และวันหยุดนักขัตฤกษ์: 10:00–22:00 น.\n\nMAYA เชียงใหม่ ชั้น G\nจันทร์–อาทิตย์: 10:00–22:00 น.\n\nCentral อุดรธานี ชั้น 1\nจันทร์–ศุกร์: 10:30–21:00 น.\nเสาร์–อาทิตย์ และวันหยุดนักขัตฤกษ์: 10:00–21:00 น.\n\nCentral Westgate จังหวัดนนทบุรี ชั้น 1\nจันทร์–อาทิตย์: 10:00–22:00 น.\n\nCentral พิษณุโลก ชั้น 1\nจันทร์–ศุกร์: 11:00–21:00 น.\nเสาร์–อาทิตย์ และวันหยุดนักขัตฤกษ์: 10:00–21:00 น.\n\nหมายเหตุ: เวลาเปิด-ปิดอาจมีการเปลี่ยนแปลงในบางวัน ช่วงเทศกาล หรือวันหยุดพิเศษ แนะนำตรวจสอบเวลาล่าสุดก่อนเดินทาง",
    },
  ],
  en: [
    {
      question: "What is Luxury29?",
      answer:
        "Luxury29 is a retailer of fashion and luxury products from leading international brands, including fashion items, footwear, and sneakers from a wide range of brands. Customers can shop through the Luxury29 website, mobile application, online channels, and physical stores.",
    },
    {
      question: "Does Luxury29 have physical stores?",
      answer:
        "Yes. Luxury29 currently has 5 physical store locations in Thailand, where customers can visit and shop in person.",
    },
    {
      question: "Where are Luxury29 stores located?",
      answer:
        "Chiang Mai: Location: Central Festival Chiangmai Shopping Center (Room No. 137, 138), 99, 99/1, 99/2 Moo 4, Super Highway Road, Fa Ham Subdistrict, Mueang Chiang Mai District, Chiang Mai 50000, Thailand. Location: Maya Chiangmai EVENT HALL, G Floor, No. 55, 55/5, Moo 5, Superhighway Road, Chang Phueak Subdistrict, Mueang Chiang Mai District, Chiang Mai 50300. Udonthani: Location: 1st floor, Central Plaza Udon Thani, 277/1-3 Pajaksinlapacom Rd, Mak Khaeng Sub-district, Mueang Udon Thani District, Udon Thani 41000, tel: 065 416 6458. Nonthaburi: Location: 199, 199/1, 199/2 Central Westgate Shopping Center, Room No. 174/1, 1st Floor, Moo 6, Kanchanaphisek Road, Saothonghin Sub-district, Bangyai District, Nonthaburi 11140. Phitsanulok: Location: 9/99 Central Phitsanulok Shopping Center, Rooms No. 129 and 130, 1st Floor, Moo 5, Phlai Chumphon Subdistrict, Mueang Phitsanulok District, Phitsanulok Province, Thailand.",
    },
    {
      question: "How many Luxury29 stores are there in Chiang Mai?",
      answer:
        "Luxury29 has 2 stores in Chiang Mai:\n\nCentral Festival Chiang Mai, 1st Floor\nMAYA Chiang Mai, Ground Floor",
    },
    {
      question: "What are Luxury29's store opening hours?",
      answer:
        "Luxury29 has 5 physical stores, all of which are open daily. Opening hours vary by location as follows:\n\nCentral Festival Chiang Mai, 1st Floor\nMonday–Thursday: 11:00 AM–9:30 PM\nFriday: 11:00 AM–10:00 PM\nSaturday–Sunday & Public Holidays: 10:00 AM–10:00 PM\n\nMAYA Chiang Mai, Ground Floor\nMonday–Sunday: 10:00 AM–10:00 PM\n\nCentral Udon Thani, 1st Floor\nMonday–Friday: 10:30 AM–9:00 PM\nSaturday–Sunday & Public Holidays: 10:00 AM–9:00 PM\n\nCentral Westgate, Nonthaburi, 1st Floor\nMonday–Sunday: 10:00 AM–10:00 PM\n\nCentral Phitsanulok, 1st Floor\nMonday–Friday: 11:00 AM–9:00 PM\nSaturday–Sunday & Public Holidays: 10:00 AM–9:00 PM\n\nNote: Store opening hours may change on certain days, during festivals, or on special holidays. We recommend checking the latest opening hours before visiting.",
    },
  ],
};

const productsAndBrands = {
  th: [
    {
      question: "Luxury29 จำหน่ายแบรนด์อะไรบ้าง?",
      answer:
        "Luxury29 จำหน่ายสินค้าแฟชั่นและสินค้าลักชัวรีจากแบรนด์ชั้นนำระดับโลก เช่น Hermès, Chanel, Louis Vuitton, Dior, Gucci, Goyard, Balenciaga รวมถึงแบรนด์แฟชั่นและสนีกเกอร์อีกหลากหลายแบรนด์ โดยแบรนด์ รุ่น สี ไซซ์ และจำนวนสินค้าอาจแตกต่างกันตามสต็อกในแต่ละช่วงเวลา",
    },
    {
      question: "Luxury29 จำหน่ายสินค้าของแท้หรือไม่?",
      answer:
        "Luxury29 จำหน่ายสินค้าของแท้ 100% สินค้าทุกชิ้นผ่านกระบวนการตรวจสอบและคัดเลือกก่อนนำมาจำหน่าย และ Luxury29 รับประกันความแท้ของสินค้าทุกชิ้นตามเงื่อนไขของบริษัท",
    },
    {
      question: "Luxury29 มีสินค้าแบรนด์เนมประเภทใดบ้าง?",
      answer:
        "Luxury29 มีสินค้าหลากหลายประเภท เช่น กระเป๋า รองเท้า เสื้อผ้า เครื่องประดับ แอ็กเซสซอรี และสินค้าแฟชั่นจากแบรนด์ชั้นนำ โดยประเภทสินค้าและสต็อกอาจแตกต่างกันตามช่วงเวลา",
    },
    {
      question: "Luxury29 มีรองเท้าแบรนด์อะไรบ้าง?",
      answer:
        "Luxury29 มีรองเท้าจากแบรนด์ลักชัวรีและสนีกเกอร์ เช่น Hermès, Chanel, Louis Vuitton, Gucci, Dior, Balenciaga, Celine, Loro Piana, Nike, Adidas, New Balance, UGG และแบรนด์อื่น ๆ โดยรุ่น สี ไซซ์ และสต็อกอาจแตกต่างกันตามช่วงเวลา",
    },
    {
      question: "สามารถตรวจสอบว่าสินค้าอยู่สาขาไหนได้หรือไม่?",
      answer:
        "ลูกค้าสามารถติดต่อ Luxury29 เพื่อสอบถามสต็อกสินค้า รุ่น สี และไซซ์ รวมถึงสาขาที่มีสินค้าที่ต้องการก่อนเดินทางไปหน้าร้าน ทั้งนี้ สต็อกสินค้าอาจเปลี่ยนแปลงได้ตลอดเวลา",
    },
    {
      question:
        "หากไม่พบสินค้าที่ต้องการบนเว็บไซต์ สามารถสอบถามเพิ่มเติมได้หรือไม่?",
      answer:
        "ได้ ลูกค้าสามารถติดต่อ Luxury29 เพื่อสอบถามสินค้า รุ่น สี หรือไซซ์ที่ต้องการเพิ่มเติม ทีมงานสามารถตรวจสอบข้อมูลสินค้าและสต็อกที่มีอยู่ในขณะนั้นให้ได้",
    },
  ],
  en: [
    {
      question: "What brands does Luxury29 carry?",
      answer:
        "Luxury29 offers fashion and luxury products from leading international brands such as Hermès, Chanel, Louis Vuitton, Dior, Gucci, Goyard, and Balenciaga, as well as a wide range of fashion and sneaker brands. Available brands, models, colors, sizes, and quantities may vary depending on stock availability.",
    },
    {
      question: "Does Luxury29 sell authentic products?",
      answer:
        "Luxury29 sells 100% authentic products. Every item goes through an inspection and selection process before being offered for sale, and Luxury29 guarantees the authenticity of every product, subject to the company's terms and conditions.",
    },
    {
      question: "What types of luxury products does Luxury29 offer?",
      answer:
        "Luxury29 offers a wide range of products, including bags, shoes, clothing, jewelry, accessories, and other fashion items from leading brands. Product categories and availability may vary depending on stock.",
    },
    {
      question: "What shoe brands does Luxury29 carry?",
      answer:
        "Luxury29 offers luxury and sneaker brands such as Hermès, Chanel, Louis Vuitton, Gucci, Dior, Balenciaga, Celine, Loro Piana, Nike, Adidas, New Balance, UGG, and many others. Models, colors, sizes, and availability may vary depending on stock.",
    },
    {
      question: "Can I check which store has a specific product in stock?",
      answer:
        "Yes. Customers can contact Luxury29 to check product availability, including the model, color, size, and store location, before visiting. Stock availability may change at any time.",
    },
    {
      question:
        "Can I inquire about a product if I cannot find it on the website?",
      answer:
        "Yes. Customers can contact Luxury29 to inquire about specific products, models, colors, or sizes. Our team can check the available products and stock at that time.",
    },
  ],
};

const orderingAndPayment = {
  th: [
    {
      question: "Luxury29 สั่งซื้อสินค้าได้ผ่านช่องทางไหนบ้าง?",
      answer:
        "ลูกค้าสามารถเลือกซื้อสินค้า Luxury29 ผ่านเว็บไซต์ แอปพลิเคชัน LINE Official Account ช่องทางโซเชียลมีเดีย และหน้าร้าน Luxury29 ทั้ง 5 สาขา",
    },
    {
      question: "สามารถสั่งซื้อสินค้าผ่านเว็บไซต์ Luxury29 ได้หรือไม่?",
      answer:
        "ได้ ลูกค้าสามารถเลือกชมสินค้า ตรวจสอบรายละเอียด และสั่งซื้อสินค้าผ่านเว็บไซต์ Luxury29 ได้ตลอด 24 ชั่วโมง โดยสามารถตรวจสอบสถานะคำสั่งซื้อผ่านบัญชีของตนเองบนเว็บไซต์",
    },
    {
      question: "สินค้าที่แสดงบนเว็บไซต์มีอยู่ที่หน้าร้านทุกสาขาหรือไม่?",
      answer:
        "ไม่จำเป็น สินค้าและสต็อกของแต่ละสาขาอาจแตกต่างกัน หากต้องการเข้าชมสินค้ารุ่นหรือไซซ์ที่ต้องการที่สาขาใดโดยเฉพาะ แนะนำให้ติดต่อ Luxury29 เพื่อตรวจสอบสต็อกก่อนเดินทาง",
    },
    {
      question: "Luxury29 รองรับการชำระเงินแบบใดบ้าง?",
      answer:
        "Luxury29 รองรับการชำระเงินผ่านบัตรเครดิต บัตรเดบิต และ PromptPay รวมถึงบริการผ่อนชำระผ่านบัตรเครดิตตามเงื่อนไขของธนาคารหรือสถาบันการเงินที่ร่วมรายการ",
    },
    {
      question: "Luxury29 มีบริการผ่อนชำระหรือไม่?",
      answer:
        "มี Luxury29 รองรับการผ่อนชำระผ่านบัตรเครดิตของธนาคารและสถาบันการเงินที่ร่วมรายการ โดยจำนวนเดือนและเงื่อนไขการผ่อนขึ้นอยู่กับธนาคาร ประเภทบัตร ยอดซื้อ และโปรโมชั่นในช่วงเวลานั้น",
    },
    {
      question: "การชำระเงินผ่านเว็บไซต์ Luxury29 ปลอดภัยหรือไม่?",
      answer:
        "Luxury29 รองรับระบบ 3D Secure สำหรับการชำระเงินด้วยบัตรเครดิตและบัตรเดบิต เพื่อเพิ่มความปลอดภัยในการทำรายการ โดยลูกค้าอาจต้องยืนยันตัวตนเพิ่มเติมตามเงื่อนไขของธนาคารผู้ออกบัตร",
    },
  ],
  en: [
    {
      question: "Where can I purchase Luxury29 products?",
      answer:
        "Customers can shop with Luxury29 through our website, mobile application, LINE Official Account, social media channels, and all 5 Luxury29 physical stores.",
    },
    {
      question: "Can I purchase products through the Luxury29 website?",
      answer:
        "Yes. Customers can browse products, view product details, and place orders through the Luxury29 website 24 hours a day. Order status can also be checked through the customer's account on the website.",
    },
    {
      question:
        "Are products shown on the website available at all Luxury29 stores?",
      answer:
        "Not necessarily. Product availability and stock may vary between stores. If you would like to view a specific model or size at a particular store, we recommend contacting Luxury29 to check availability before visiting.",
    },
    {
      question: "What payment methods does Luxury29 accept?",
      answer:
        "Luxury29 accepts credit cards, debit cards, and PromptPay. Credit card installment plans are also available according to the terms and conditions of participating banks or financial institutions.",
    },
    {
      question: "Does Luxury29 offer installment payments?",
      answer:
        "Yes. Luxury29 offers installment payment options through participating banks and financial institutions. The available installment period and terms depend on the bank, card type, purchase amount, and current promotions.",
    },
    {
      question: "Is payment through the Luxury29 website secure?",
      answer:
        "Luxury29 supports 3D Secure for credit and debit card payments to provide additional security during transactions. Customers may be required to complete additional verification according to the terms and conditions of their card-issuing bank.",
    },
  ],
};

const shippingAndPickup = {
  th: [
    {
      question: "Luxury29 มีค่าจัดส่งเท่าไหร่?",
      answer:
        "ค่าจัดส่งมาตรฐานสำหรับคำสั่งซื้อภายในประเทศไทยอยู่ที่ 200 บาทต่อคำสั่งซื้อ ส่วนการจัดส่งไปต่างประเทศ ค่าจัดส่งจะขึ้นอยู่กับประเทศปลายทาง น้ำหนัก และขนาดของพัสดุ",
    },
    {
      question: "Luxury29 จัดส่งสินค้าใช้เวลากี่วัน?",
      answer:
        "การจัดส่งภายในประเทศไทยแบบมาตรฐานใช้เวลาประมาณ 1–3 วันทำการ ทั้งนี้ ระยะเวลาจัดส่งอาจแตกต่างกันตามพื้นที่ปลายทางและบริษัทขนส่ง",
    },
    {
      question: "สามารถรับสินค้าที่หน้าร้าน Luxury29 ได้หรือไม่?",
      answer:
        "ได้ ลูกค้าสามารถเลือกมารับสินค้าที่หน้าร้าน Luxury29 สาขาที่ให้บริการ โดยสามารถรับสินค้าได้หลังจากคำสั่งซื้อผ่านการดำเนินการประมาณ 1–2 วันทำการ ทั้งนี้ควรรอการยืนยันก่อนเดินทางไปรับสินค้า",
    },
    {
      question: "หากเลือกรับสินค้าที่ร้าน ต้องเตรียมอะไรบ้าง?",
      answer:
        "ลูกค้าควรเตรียมอีเมลยืนยันคำสั่งซื้อ บัตรประชาชน หนังสือเดินทาง หรือใบขับขี่ตัวจริง และบัตรเครดิตหรือบัตรเดบิตที่ใช้ชำระเงิน หากไม่มีบัตรตัวจริง สามารถแสดงบัตรผ่าน Mobile Application ของธนาคารได้ตามเงื่อนไขของบริษัท",
    },
    {
      question: "Luxury29 จัดส่งสินค้าไปต่างประเทศหรือไม่?",
      answer:
        "Luxury29 ให้บริการจัดส่งสินค้าไปต่างประเทศผ่าน DHL โดยค่าจัดส่งและระยะเวลาจัดส่งขึ้นอยู่กับประเทศปลายทาง น้ำหนัก และขนาดของพัสดุ",
    },
  ],
  en: [
    {
      question: "How much is the shipping fee?",
      answer:
        "The standard domestic shipping fee within Thailand is THB 200 per order. For international shipping, the shipping fee depends on the destination country, package weight, and package size.",
    },
    {
      question: "How long does delivery take?",
      answer:
        "Standard delivery within Thailand typically takes approximately 1–3 business days. Delivery times may vary depending on the destination and shipping provider.",
    },
    {
      question: "Can I pick up my order at a Luxury29 store?",
      answer:
        "Yes. Customers can choose store pickup at participating Luxury29 locations. Orders are generally ready for pickup within approximately 1–2 business days after processing. Customers should wait for pickup confirmation before visiting the store.",
    },
    {
      question: "What do I need to bring when picking up my order at a store?",
      answer:
        "Customers should bring their order confirmation email, an original identification document, such as a Thai national ID card, passport, or driver's license, as well as the credit or debit card used for payment. If the physical card is unavailable, a card displayed through the bank's mobile application may be accepted, subject to the company's terms and conditions.",
    },
    {
      question: "Does Luxury29 ship internationally?",
      answer:
        "Yes. Luxury29 provides international shipping via DHL. Shipping fees and delivery times depend on the destination country, package weight, and package size.",
    },
  ],
};

const returnsAndAfterSales = {
  th: [
    {
      question: "Luxury29 สามารถเปลี่ยนหรือคืนสินค้าได้หรือไม่?",
      answer:
        "Luxury29 ขอสงวนสิทธิ์ในการรับเปลี่ยนหรือคืนสินค้าตามเงื่อนไขของบริษัท ลูกค้าควรตรวจสอบรายละเอียดสินค้า รุ่น สี ไซซ์ และเงื่อนไขการสั่งซื้อก่อนยืนยันคำสั่งซื้อ หากได้รับสินค้าไม่ตรงกับคำสั่งซื้อหรือพบปัญหาที่เกิดจากทางร้าน กรุณาติดต่อ Luxury29 โดยเร็วเพื่อให้เจ้าหน้าที่ตรวจสอบ",
    },
    {
      question: "Luxury29 มีการรับประกันสินค้าหรือไม่?",
      answer:
        "Luxury29 มีบริการรับประกันสินค้าและบริการหลังการขาย โดยเงื่อนไขการรับประกันแตกต่างกันตามประเภทสินค้า แบรนด์ และเงื่อนไขของแต่ละรายการ ลูกค้าสามารถตรวจสอบรายละเอียดการรับประกันของสินค้าแต่ละรายการก่อนสั่งซื้อ",
    },
  ],
  en: [
    {
      question: "Can I return or exchange a Luxury29 product?",
      answer:
        "Luxury29 accepts returns or exchanges subject to the company's terms and conditions. Customers are advised to carefully check the product details, model, color, size, and applicable terms before confirming an order. If the product received does not match the order or there is an issue caused by Luxury29, please contact us as soon as possible so our team can investigate.",
    },
    {
      question: "Does Luxury29 provide a product warranty?",
      answer:
        "Luxury29 provides product warranty and after-sales services. Warranty terms vary depending on the product type, brand, and individual product conditions. Customers can check the warranty details for each product before placing an order.",
    },
  ],
};

const faqGroups = [
  aboutLuxury29,
  productsAndBrands,
  orderingAndPayment,
  shippingAndPickup,
  returnsAndAfterSales,
];

const contentMeta = {
  th: {
    title: "FAQs",
    subtitle: "ค้นหาคำตอบสำหรับคำถามที่พบบ่อยเกี่ยวกับ Luxury29",
    all: "หัวข้อทั้งหมด",
    more: "แสดงคำถามเพิ่มเติม",
    less: "แสดงคำถามน้อยลง",
    cats: [
      "รู้จัก Luxury29 และหน้าร้าน",
      "สินค้าและแบรนด์",
      "การสั่งซื้อและชำระเงิน",
      "การจัดส่งและรับสินค้า",
      "เปลี่ยน/คืนสินค้า และบริการหลังการขาย",
    ],
  },
  en: {
    title: "FAQs",
    subtitle: "Find answers to the most common questions about Luxury29.",
    all: "ALL TOPICS",
    more: "SHOW MORE QUESTIONS",
    less: "SHOW FEWER QUESTIONS",
    cats: [
      "About Luxury29 & Our Stores",
      "Products & Brands",
      "Ordering & Payment",
      "Shipping & Store Pickup",
      "Returns, Exchanges & After-Sales Service",
    ],
  },
};

const getContent = (locale) => ({
  ...contentMeta[locale],
  items: faqGroups
    .flatMap((group) => group[locale])
    .map(({ question, answer }) => [question, answer]),
});

const featured = {
  en: [
    [2, "Where are Luxury29 stores located?"],
    [4, "What time does the shop open and close?"],
    [11, "What channels can I order products?"],
    [22, "Can the item be returned or not?"],
    [21, "Shipping"],
    [17, "Shipping costs"],
    [18, "Transit time"],
    [12, "How can I check my order status?"],
    [14, "What payment methods are accepted?"],
    [14, "Which bank accounts can I transfer money to for payment?"],
    [16, "Is it safe to transfer money to your company account?"],
    [13, "Can I change my delivery address after placing an order?"],
  ],
  th: [
    [2, "ร้าน Luxury29 มีสาขาอยู่ที่ไหนบ้าง?"],
    [4, "ร้านเปิดและปิดกี่โมง?"],
    [11, "สั่งซื้อสินค้าได้ช่องทางใดบ้าง?"],
    [22, "สามารถส่งคืนสินค้าได้หรือไม่?"],
    [21, "การจัดส่งสินค้า"],
    [17, "ค่าจัดส่งสินค้า"],
    [18, "ระยะเวลาในการจัดส่ง"],
    [12, "ตรวจสอบสถานะคำสั่งซื้อได้อย่างไร?"],
    [14, "รองรับวิธีการชำระเงินแบบใดบ้าง?"],
    [14, "สามารถโอนเงินชำระไปยังบัญชีธนาคารใดได้บ้าง?"],
    [16, "การโอนเงินเข้าบัญชีบริษัทปลอดภัยหรือไม่?"],
    [13, "สามารถเปลี่ยนที่อยู่จัดส่งหลังสั่งซื้อได้หรือไม่?"],
  ],
};

export async function getServerSideProps(ctx) {
  return {
    props: {
      ...(await serverSideTranslations(ctx?.locale, ["common"])),
      token: ctx?.req?.cookies?.token || null,
    },
  };
}

const storeLocations = [
  {
    province: "Chiang Mai",
    branches: [
      {
        href: "https://maps.app.goo.gl/Ure2aEqktuV71uQy6",
        text: "Location: Central Festival Chiangmai Shopping Center (Room No. 137, 138), 99, 99/1, 99/2 Moo 4, Super Highway Road, Fa Ham Subdistrict, Mueang Chiang Mai District, Chiang Mai 50000, Thailand",
      },
      {
        href: "https://maps.app.goo.gl/W77CgKvmpe9Pv92B6",
        text: "Location: Maya Chiangmai EVENT HALL, G Floor, No. 55, 55/5, Moo 5, Superhighway Road, Chang Phueak Subdistrict, Mueang Chiang Mai District, Chiang Mai 50300",
      },
    ],
  },
  {
    province: "Udonthani",
    branches: [
      {
        href: "https://maps.app.goo.gl/CfEGzMF72BBN1R2m7",
        text: "Location: 1st floor, Central Plaza Udon Thani, 277/1-3 Pajaksinlapacom Rd, Mak Khaeng Sub-district, Mueang Udon Thani District, Udon Thani 41000, tel: 065 416 6458",
      },
    ],
  },
  {
    province: "Nonthaburi",
    branches: [
      {
        href: "https://maps.app.goo.gl/bVWALUzEJn5x6PcV9",
        text: "Location: 199, 199/1, 199/2 Central Westgate Shopping Center, Room No. 174/1, 1st Floor, Moo 6, Kanchanaphisek Road, Saothonghin Sub-district, Bangyai District, Nonthaburi 11140",
      },
    ],
  },
  {
    province: "Phitsanulok",
    branches: [
      {
        href: "https://maps.app.goo.gl/JJH5Fkd1vRBus8Qd9",
        text: "Location: 9/99 Central Phitsanulok Shopping Center, Rooms No. 129 and 130, 1st Floor, Moo 5, Phlai Chumphon Subdistrict, Mueang Phitsanulok District, Phitsanulok Province, Thailand",
      },
    ],
  },
];

function StoreLocationsAnswer() {
  return (
    <div className="space-y-4">
      {storeLocations.map(({ province, branches }) => (
        <div key={province}>
          <strong className="mb-1 block">{province}</strong>
          <div className="space-y-1">
            {branches.map(({ href, text }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-black hover:text-black hover:underline"
              >
                <Image
                  src="/static/images/ic_location.png"
                  alt="location"
                  width={14}
                  height={14}
                  className="mt-1 h-[14px] w-[14px] shrink-0 object-contain"
                />
                <span>{text}</span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Content({ data, locale }) {
  const [category, setCategory] = useState(null),
    [open, setOpen] = useState(null),
    [all, setAll] = useState(false);
  const visible = useMemo(() => {
    if (category !== null) {
      const [s, e] = ranges[category];
      return Array.from({ length: e - s }, (_, i) => ({
        itemIndex: s + i,
        title: data.items[s + i][0],
      }));
    }
    if (!all)
      return featured[locale].map(([itemIndex, title]) => ({
        itemIndex,
        title,
      }));
    const used = new Set(featured[locale].map(([itemIndex]) => itemIndex));
    return [
      ...featured[locale].map(([itemIndex, title]) => ({ itemIndex, title })),
      ...data.items
        .map((item, itemIndex) => ({ itemIndex, title: item[0] }))
        .filter((item) => !used.has(item.itemIndex)),
    ].slice(0, 24);
  }, [category, all, data, locale]);
  return (
    <div className="grid items-start gap-[22px] lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-[34px]">
      <aside className="overflow-hidden rounded-md border border-[#dedede] bg-white lg:mt-[104px]">
        <button
          type="button"
          className={`w-full px-5 py-4 text-left text-[15px] font-bold uppercase transition-colors ${
            category === null
              ? "bg-[#eeeeee]"
              : "bg-[#f5f5f5] hover:bg-[#eeeeee]"
          }`}
          onClick={() => {
            setCategory(null);
            setOpen(null);
            setAll(false);
          }}
        >
          {data.all}
        </button>
        {data.cats.map((name, i) => {
          const Icon = icons[i];
          return (
            <button
              key={name}
              className={`grid min-h-[52px] w-full grid-cols-[24px_1fr_auto] items-center gap-[14px] border-0 border-t border-[#e9e9e9] px-4 py-[10px] text-left text-[#151515] transition-colors [&>svg]:text-[21px] ${
                category === i ? "bg-[#f5f5f5]" : "bg-white hover:bg-[#f5f5f5]"
              }`}
              onClick={() => {
                setCategory(category === i ? null : i);
                setOpen(null);
                setAll(false);
              }}
            >
              <Icon />
              <span className="text-sm leading-[1.35]">{name}</span>
              <em className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#f1f1f1] text-xs not-italic">
                {ranges[i][1] - ranges[i][0]}
              </em>
            </button>
          );
        })}
      </aside>
      <section className="overflow-hidden rounded-md border border-[#ddd] bg-white">
        {visible.map(({ itemIndex, title }, position) => {
          const item = data.items[itemIndex],
            openKey = `${itemIndex}-${title}`,
            expanded = open === openKey;
          return (
            <article
              key={openKey}
              className="border-t border-[#ddd] first:border-t-0"
            >
              <button
                className={`grid min-h-12 w-full grid-cols-[24px_1fr_16px] items-center border-0 px-[14px] py-3 text-left text-[13px] font-semibold text-[#111] sm:grid-cols-[28px_1fr_18px] sm:px-5 sm:py-[11px] sm:pl-[26px] sm:text-sm [&>svg]:justify-self-end [&>svg]:text-[13px] ${
                  expanded ? "bg-[#fafafa]" : "bg-white"
                }`}
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : openKey)}
              >
                <span>{position + 1}.</span>
                <span>{title}</span>
                {expanded ? <MinusOutlined /> : <PlusOutlined />}
              </button>
              {expanded && (
                <div className="whitespace-pre-line bg-[#fafafa] px-[38px] pb-[18px] pt-2 text-[13px] leading-[1.6] sm:px-[46px] sm:pb-5 sm:pl-[54px] sm:pt-[10px] sm:text-sm">
                  {itemIndex === 2 ? <StoreLocationsAnswer /> : item[1]}
                </div>
              )}
            </article>
          );
        })}
        {category === null && (
          <button
            className="flex min-h-[42px] w-full items-center justify-center gap-3 border-0 border-t border-[#ddd] bg-gradient-to-b from-[#fafafa] to-[#f2f2f2] text-[13px] font-semibold text-[#111]"
            onClick={() => {
              setAll(!all);
              setOpen(null);
            }}
          >
            {all ? data.less : data.more}
            <span
              className={`h-2 w-2 border-b-[1.5px] border-r-[1.5px] border-[#111] ${
                all ? "rotate-[225deg]" : "rotate-45"
              }`}
            />
          </button>
        )}
      </section>
    </div>
  );
}

export default function Faq({
  token,
  categoryMenu,
  hasSale,
  hasLive,
  dataSeo,
  dataReferrer,
}) {
  const router = useRouter(),
    { t } = useTranslation("common"),
    locale = router.locale === "th" ? "th" : "en",
    data = getContent(locale);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.items.map(([name, text]) => ({
      "@type": "Question",
      name,
      acceptedAnswer: { "@type": "Answer", text },
    })),
  };
  return (
    <App
      token={token}
      categoryMenu={categoryMenu}
      hasSale={hasSale}
      hasLive={hasLive}
      dataSeo={dataSeo}
      dataReferrer={dataReferrer}
    >
      <Head>
        <title>FAQs | Luxury29</title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <main className="py-[18px] text-[#111] sm:pb-10 sm:pt-7">
        <Row justify="center">
          <Col xs={22} sm={22} md={22} lg={22} xl={20}>
            <Breadcrumb
              className="mb-[14px] text-[13px]"
              items={[
                { key: "home", title: <Link href="/">{t("home")}</Link> },
                { key: "faq", title: data.title },
              ]}
            />
            <div className="mb-6 lg:mb-[-78px] lg:w-[310px]">
              <h1 className="mb-1 text-4xl font-bold leading-[1.12] tracking-[-0.7px] sm:text-[40px] lg:text-5xl">
                {data.title}
              </h1>
              <p className="m-0 max-w-[290px] text-sm leading-[1.55] text-[#666]">
                {data.subtitle}
              </p>
            </div>
            <Content data={data} locale={locale} />
          </Col>
        </Row>
      </main>
    </App>
  );
}
